/**
 * FACE WEB DE `services/stripe.tsx` — résolue par l'extension de plateforme de Metro.
 *
 * Son rôle reste de ne PAS importer `@stripe/stripe-react-native`, qui casse le bundle web à la
 * compilation (voir le fichier natif pour pourquoi).
 *
 * ⚠️ MÊME EXTENSION `.tsx` QUE LA FACE NATIVE — non négociable. Metro parcourt `sourceExts` dans
 * l'ordre `ts tsx mjs js …` et essaie `nom.<plateforme>.<ext>` avant `nom.<ext>` POUR CHAQUE
 * extension : avec une face native en `.ts`, ce fichier ne serait jamais atteint.
 *
 * POURQUOI `payer` NE PEUT PAS ÊTRE UNE SIMPLE FONCTION ICI, et comment le contrat tient quand même.
 * Stripe exige que `<PaymentElement>` soit MONTÉ avant toute confirmation : il n'existe pas
 * d'équivalent web de « ouvre une feuille et rends-moi le résultat ». Le hook rend donc un couple —
 * `payer`, qui demande, et `Formulaire`, que l'écran monte. `payer` renvoie une promesse que la
 * confirmation résoudra. Vu de l'écran, le contrat du lot (a) est intact : on appelle `payer`, on
 * reçoit une `IssuePaiement`.
 */
import React, { useCallback, useRef, useState, type ReactNode } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';
import { capturerIncident } from './sentry';
import type { EtatPaiement } from './paiementStatut';

/**
 * COUPE-CIRCUIT, ET NON PLUS DESCRIPTION DE PLATEFORME — NE PAS SUPPRIMER COMME REDONDANT.
 *
 * Passé à `true` en DERNIER du lot (b), une fois le formulaire, la confirmation, la route de
 * retour et la garde de montant vérifiés. Sa valeur n'est plus un constat technique mais une
 * DÉCISION, et il reste le moyen de refermer le paiement sans déployer de code — si Stripe tombe,
 * si un moyen de paiement se comporte mal, ou le temps de comprendre un incident.
 *
 * Il doit rester consulté AVANT `payer` : c'est lui qui empêche de créer une intention de paiement
 * chez Stripe pour un parcours qu'on sait fermé.
 */
export const PAIEMENT_DISPONIBLE = true;

/**
 * Chargé UNE SEULE FOIS, au premier besoin. `loadStripe` injecte un script distant : l'appeler au
 * rendu le relancerait à chaque passage, et hors du paiement il n'a rien à faire dans la page.
 */
let stripePromise: Promise<Stripe | null> | null = null;
function stripe(): Promise<Stripe | null> {
  stripePromise ??= loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
}

/** Passe-plat : `<Elements>` est monté par `Formulaire`, au moment où un paiement est demandé. */
export function StripeProvider({ children }: { children?: ReactNode; publishableKey?: string }) {
  return <>{children}</>;
}

export type IssuePaiement =
  | { etat: EtatPaiement; message?: string }
  | { etat: 'redirige' };

export interface DemandePaiement {
  clientSecret: string;
  returnUrl: string;
}

/**
 * Bouton de confirmation, à l'intérieur d'`<Elements>` — `useStripe`/`useElements` n'existent
 * QUE là.
 *
 * `redirect` N'EST PAS MIS À `'if_required'`, ET C'EST LE CŒUR DU LOT. Six des huit moyens du
 * compte redirigent — bancontact, eps, klarna, link, mb_way, satispay, amazon_pay ; seule `card`
 * peut aboutir sans quitter la page. La redirection est donc le chemin NORMAL. Avec
 * `if_required`, Stripe tenterait de rester sur place et l'issue majoritaire deviendrait un cas
 * particulier mal éprouvé.
 *
 * Quand la redirection a lieu, `confirmPayment` NE REND PAS : le navigateur est déjà parti. Le
 * `return` qui suit n'est donc atteint que sur erreur immédiate, ou pour un moyen sans
 * redirection.
 */
function Confirmation({ returnUrl, onIssue }: { returnUrl: string; onIssue: (i: IssuePaiement) => void }) {
  const stripeJs = useStripe();
  const elements = useElements();
  const [enCours, setEnCours] = useState(false);

  const confirmer = async () => {
    if (!stripeJs || !elements) {
      capturerIncident('coaching_stripe_non_charge', 'paiement');
      return onIssue({ etat: 'failed' });
    }
    setEnCours(true);
    try {
      const { error } = await stripeJs.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
      });

      /**
       * CE POINT N'EST ATTEINT QU'EN CAS D'ERREUR, et le TYPE de `confirmPayment` le dit :
       * sans `redirect: 'if_required'`, sa seule forme de retour est `{ error }`. Quand la
       * confirmation part, le navigateur quitte la page — il n'y a personne pour recevoir un
       * succès. C'est la route de retour qui l'apprendra, via `redirect_status`.
       *
       * `validation_error` = formulaire incomplet : l'utilisateur corrige, ce n'est pas un
       * incident. Tout le reste en est un, et remonte par un CODE, jamais par `error.message`.
       */
      // FORMULAIRE INCOMPLET : le champ fautif porte déjà son message (PaymentElement), et la
      // personne corrige. Résoudre ici démonterait le formulaire — il faudrait tout recommencer, avec
      // une nouvelle intention de paiement orpheline à chaque essai.
      if (error.type === 'validation_error') return;
      capturerIncident('coaching_confirmation_refusee', 'paiement');
      onIssue({ etat: 'failed', message: error.message });
    } catch {
      capturerIncident('coaching_confirmation_exception', 'paiement');
      onIssue({ etat: 'failed' });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div data-testid="paiement-formulaire">
      <PaymentElement />
      <button type="button" data-testid="paiement-confirmer" disabled={enCours} onClick={() => void confirmer()}>
        {enCours ? '…' : 'Payer'}
      </button>
    </div>
  );
}

export function usePaiementCoaching() {
  const [demande, setDemande] = useState<DemandePaiement | null>(null);
  const resoudre = useRef<((i: IssuePaiement) => void) | null>(null);

  const payer = useCallback(
    (d: DemandePaiement) =>
      new Promise<IssuePaiement>((res) => {
        resoudre.current = res;
        setDemande(d);
      }),
    [],
  );

  const rendre = useCallback((issue: IssuePaiement) => {
    setDemande(null);
    resoudre.current?.(issue);
    resoudre.current = null;
  }, []);

  const Formulaire = useCallback(() => {
    if (!demande) return null;
    return (
      <Elements stripe={stripe()} options={{ clientSecret: demande.clientSecret }}>
        <Confirmation returnUrl={demande.returnUrl} onIssue={rendre} />
      </Elements>
    );
  }, [demande, rendre]);

  return { payer, Formulaire };
}
